import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Radar as RadarIcon, ShieldAlert, AlertTriangle, AlertCircle, XCircle,
    Copy, Check, Loader2, Lock, Unlock, ChevronDown, Flame, TrendingUp,
    Twitter, Send, Globe, ArrowRight, ArrowUpRight, ArrowDown, ArrowUp, Search, X,
    Star, GraduationCap,
} from "lucide-react";
import {
    useScannerFeed, type ScannerToken, type ScannerTier, type ScannerFlag,
    type ScannerCategory, type ScannerSocial, type Momentum, type ScannerFeed,
    type RadarPricing, type ChainSentiment,
} from "@/hooks/useScannerFeed";
import { useRadarAuth } from "@/hooks/useRadarAuth";
import { useRadarAccess } from "@/hooks/useRadarAccess";
import { EXPLORER_TOKEN } from "@/lib/config";

// Compact PIGGY amount for the unlock requirement copy (e.g. "5M").
function fmtCompactPiggy(v: string): string {
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toFixed(0);
}

// Compact PIGGY amount from a raw 18-decimal bigint (for the on-chain stake read).
function fmtCompactPiggyBig(v: bigint): string {
    return fmtCompactPiggy((Number(v) / 1e18).toString());
}

// tier colour: low (few red flags) = green, mixed = amber, high = red. NOT a
// "safe/unsafe" claim — it's how many risk factors tripped.
const TIER: Record<ScannerTier, { text: string; chip: string; dot: string }> = {
    low:   { text: "text-piggy-gold-400", chip: "bg-piggy-gold-500/15 text-piggy-gold-300 ring-piggy-gold-500/30", dot: "bg-piggy-gold-400" },
    mixed: { text: "text-amber-400",      chip: "bg-amber-500/15 text-amber-300 ring-amber-500/30",               dot: "bg-amber-400" },
    high:  { text: "text-red-400",        chip: "bg-red-500/15 text-red-300 ring-red-500/30",                     dot: "bg-red-400" },
};

// Momentum = neutral fact, never a prediction.
const MOMENTUM: Record<Momentum, { label: string; chip: string; icon: typeof Flame | null }> = {
    surging: { label: "Surging", chip: "bg-piggy-pink-500/20 text-piggy-pink-200 ring-piggy-pink-500/40", icon: Flame },
    rising:  { label: "Rising",  chip: "bg-piggy-gold-500/15 text-piggy-gold-300 ring-piggy-gold-500/30", icon: TrendingUp },
    flat:    { label: "Flat",    chip: "bg-white/[0.05] text-piggy-cream/50 ring-white/10", icon: null },
    cooling: { label: "Cooling", chip: "bg-amber-500/12 text-amber-300 ring-amber-500/25", icon: null },
    falling: { label: "Falling", chip: "bg-red-500/12 text-red-300 ring-red-500/25", icon: null },
};

const CATEGORY_LABEL: Record<ScannerCategory, string> = {
    established: "Established",
    "new-launch": "New",
    listed: "Listed",
    "hidden-gem": "Gem",
    bonding: "Bonding",
};

type FilterKey = "all" | "fresh" | ScannerCategory | ScannerTier;
const FRESH_HOURS = 12;
const FILTERS: { key: FilterKey; label: string; dot?: string }[] = [
    { key: "all",          label: "All" },
    { key: "fresh",        label: "🔥 Fresh <12h" },
    { key: "bonding",      label: "🎓 Graduating" },
    { key: "established",  label: "Established" },
    { key: "new-launch",   label: "New launches" },
    { key: "listed",       label: "Listed" },
    { key: "hidden-gem",   label: "Hidden gems" },
    { key: "low",          label: "Fewest flags", dot: TIER.low.dot },
    { key: "mixed",        label: "Mixed",        dot: TIER.mixed.dot },
    { key: "high",         label: "High risk",    dot: TIER.high.dot },
];
const isFresh = (t: ScannerToken) => t.ageHours != null && t.ageHours < FRESH_HOURS;

const CATEGORY_KEYS: ScannerCategory[] = ["established", "new-launch", "listed", "hidden-gem", "bonding"];
const isCategory = (k: FilterKey): k is ScannerCategory => (CATEGORY_KEYS as string[]).includes(k);
const isTier = (k: FilterKey): k is ScannerTier => k === "low" || k === "mixed" || k === "high";

type SortKey = "volume" | "score" | "liquidity" | "price" | "mcap" | "change" | "holders" | "buy" | "top10" | "newest";
const SORTS: Record<SortKey, (a: ScannerToken, b: ScannerToken) => number> = {
    volume:    (a, b) => (b.volumeH24 ?? 0) - (a.volumeH24 ?? 0),
    score:     (a, b) => b.score - a.score,
    liquidity: (a, b) => (b.liquidityUsd ?? 0) - (a.liquidityUsd ?? 0),
    price:     (a, b) => (b.priceUsd ?? 0) - (a.priceUsd ?? 0),
    mcap:      (a, b) => (b.marketCap ?? 0) - (a.marketCap ?? 0),
    change:    (a, b) => (b.priceChangeH24 ?? 0) - (a.priceChangeH24 ?? 0),
    holders:   (a, b) => (b.holderCount ?? 0) - (a.holderCount ?? 0),
    buy:       (a, b) => (b.signals.buyPressureH24 ?? 0) - (a.signals.buyPressureH24 ?? 0),
    top10:     (a, b) => (a.top10HolderPct ?? 999) - (b.top10HolderPct ?? 999), // lower = better, asc
    newest:    (a, b) => (a.ageHours ?? 1e9) - (b.ageHours ?? 1e9),
};

// Column layout. Widths are FIXED px so the header and every row (free + faded)
// align perfectly and the table scrolls horizontally on small screens. `hint`
// renders a hover/tap tooltip on the header so the metric explains itself.
const COLS: { key: string; label: string; sort?: SortKey; align: "left" | "right" | "center"; hint?: string }[] = [
    { key: "rank",    label: "#",        align: "right" },
    { key: "token",   label: "Token",    align: "left" },
    { key: "score",   label: "Score",    sort: "score", align: "center",
      hint: "Risk-screen score out of 100 — how many transparency checks pass (verified source, liquidity depth, sells recorded, holder spread…). Higher = fewer red flags, NOT a prediction." },
    { key: "price",   label: "Price",    sort: "price", align: "right" },
    { key: "mcap",    label: "MCap",     sort: "mcap", align: "right",
      hint: "Market cap — token price × circulating supply, as reported by the market data." },
    { key: "change",  label: "24h",      sort: "change", align: "right" },
    { key: "vol",     label: "Vol 24h",  sort: "volume", align: "right" },
    { key: "liq",     label: "Liquidity", sort: "liquidity", align: "right",
      hint: "Dollars actually pooled in the trading pair. Deeper liquidity = less slippage and harder to manipulate. For bonding tokens this is the curve reserve." },
    { key: "holders", label: "Holders",  sort: "holders", align: "right",
      hint: "Number of wallets holding the token. The arrow shows the trend over the last hours." },
    { key: "buy",     label: "Buy/Sell", sort: "buy", align: "left",
      hint: "Share of the last 24h trades that were buys. Above ~60% = buy-heavy flow; below ~40% = sell-heavy. A fact about order flow, not advice. Bonding rows show graduation progress instead." },
    { key: "top10",   label: "Top10",    sort: "top10", align: "right",
      hint: "How much of the supply the 10 largest real wallets hold (LP, burn and launchpad vault excluded). High concentration = a few wallets can move the price." },
    { key: "tax",     label: "Tax",      align: "right",
      hint: "Launchpad buy/sell tax taken on each trade (flap.sh tokens only)." },
    { key: "age",     label: "Age",      sort: "newest", align: "right" },
    { key: "signal",  label: "Signal",   align: "left",
      hint: "A neutral read of the current data: posture (accumulating, cooling, firming…) plus momentum. Descriptive only — never a buy/sell signal." },
    { key: "act",     label: "",         align: "right" },
];
const GRID = "grid grid-cols-[28px_168px_50px_86px_82px_68px_84px_84px_106px_102px_52px_58px_44px_134px_86px] gap-x-2.5 items-center px-3";

// How many rows a non-staker sees fully before the feed fades into the gate.
const FREE_TASTE = 6;

export default function Radar() {
    const auth = useRadarAuth();
    const { data, loading, error } = useScannerFeed(auth.token);
    const access = useRadarAccess(Boolean(auth.token), data.pricing?.stakePiggy);
    const [filter, setFilter] = useState<FilterKey>("all");
    const [sort, setSort] = useState<SortKey>("volume");
    const [expanded, setExpanded] = useState<string | null>(null);
    const [query, setQuery] = useState("");

    const shown = useMemo(() => {
        let rows = data.feed;
        if (filter === "fresh") rows = rows.filter(isFresh);
        else if (isCategory(filter)) rows = rows.filter((t) => t.category === filter);
        else if (isTier(filter)) rows = rows.filter((t) => t.tier === filter);
        const q = query.trim().toLowerCase();
        if (q) rows = rows.filter((t) =>
            t.symbol.toLowerCase().includes(q)
            || (t.name ?? "").toLowerCase().includes(q)
            || t.token.toLowerCase().includes(q));
        // "Fresh" defaults to newest-first; "Graduating" sorts by progress so
        // the tokens closest to bonding sit on top (on-chain curves have no
        // 24h volume figure to rank by).
        if (filter === "bonding") return [...rows].sort((a, b) => (b.flap?.progress ?? 0) - (a.flap?.progress ?? 0));
        return [...rows].sort(filter === "fresh" ? SORTS.newest : SORTS[sort]);
    }, [data.feed, filter, sort, query]);

    const trending = useMemo(() => {
        const byAddr = new Map(data.feed.map((t) => [t.token.toLowerCase(), t]));
        return data.trending
            .map((addr) => byAddr.get(addr.toLowerCase()))
            .filter((t): t is ScannerToken => Boolean(t));
    }, [data.feed, data.trending]);

    const hasFeed = data.feed.length > 0;
    const locked = data.locked;
    const unlocked = access.unlocked || auth.ethUnlocked;

    const freeCount = unlocked ? shown.length : Math.min(FREE_TASTE, shown.length);
    const freeRows = shown.slice(0, freeCount);
    const fadedRows = unlocked ? [] : shown.slice(freeCount, freeCount + 4);

    const toggleSort = (k?: SortKey) => { if (k) setSort(k); };
    const toggleExpand = (addr: string) => setExpanded((cur) => (cur === addr ? null : addr));

    return (
        <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-wrap items-end justify-between gap-4 mb-6"
            >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
                    <h1 className="display text-3xl md:text-4xl font-black tracking-tighter leading-none">
                        Piggy <span className="gradient-text-shimmer">Radar</span>
                    </h1>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full glass-pink text-[11px] font-semibold text-piggy-pink-200">
                        <RadarIcon className="w-3 h-3" /> Alpha scanner · Robinhood Chain
                    </span>
                    <span className="text-piggy-cream/45 text-xs hidden md:inline">
                        Every tradeable token, scored &amp; screened — the facts behind each one, so you decide.
                    </span>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                    {!locked && (unlocked ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-piggy-gold-500/15 text-piggy-gold-300 ring-1 ring-piggy-gold-500/30">
                            <Unlock className="w-3 h-3" /> Premium unlocked
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-piggy-pink-500/15 text-piggy-pink-200 ring-1 ring-piggy-pink-500/30">
                            <Lock className="w-3 h-3" /> Preview — stake to unlock the full feed
                        </span>
                    ))}
                    <div className="text-[11px] text-piggy-cream/40 tabular-nums">
                        {hasFeed || locked ? `${data.count} tokens · updated ${relTime(data.scannedAt)}` : "Fetching latest scan…"}
                        {error && <span className="ml-1.5 text-amber-400/80">· showing last scan</span>}
                    </div>
                </div>
            </motion.div>

            {/* Body */}
            {locked ? (
                <LockedState counts={data.counts} auth={auth} />
            ) : loading && !hasFeed ? (
                <SkeletonTable />
            ) : !hasFeed ? (
                <EmptyState error={error} />
            ) : (
                <>
                    {data.sentiment && <SentimentBar s={data.sentiment} />}
                    <StatsRibbon feed={data.feed} />
                    {trending.length > 0 && <TrendingRow tokens={trending} />}

                    {/* Filter controls */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        {FILTERS.map((f) => {
                            const count =
                                f.key === "all" ? data.feed.length
                                : f.key === "fresh" ? data.feed.filter(isFresh).length
                                : isCategory(f.key) ? data.feed.filter((t) => t.category === f.key).length
                                : data.feed.filter((t) => t.tier === f.key).length;
                            if (f.key !== "all" && count === 0) return null;
                            const active = filter === f.key;
                            return (
                                <button key={f.key} onClick={() => setFilter(f.key)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                                        active ? "bg-piggy-pink-500/20 text-piggy-pink-200 ring-1 ring-piggy-pink-500/40" : "text-piggy-cream/45 hover:text-piggy-cream ring-1 ring-transparent"
                                    }`}>
                                    {f.dot && <span className={`w-1.5 h-1.5 rounded-full ${f.dot}`} />}
                                    {f.label}<span className="tabular-nums opacity-60">{count}</span>
                                </button>
                            );
                        })}
                        <div className="ml-auto flex items-center gap-3">
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-piggy-cream/30 pointer-events-none" />
                                <input
                                    type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search token…" aria-label="Search tokens"
                                    className="w-40 sm:w-52 rounded-full bg-white/[0.03] border border-white/[0.08] focus:border-piggy-pink-500/50 outline-none pl-8 pr-7 py-1 text-xs text-piggy-cream placeholder:text-piggy-cream/30 transition-colors"
                                />
                                {query && (
                                    <button onClick={() => setQuery("")} aria-label="Clear search"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-piggy-cream/40 hover:text-piggy-cream">
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                            <span className="text-[11px] text-piggy-cream/30 hidden lg:inline">Click a column header to sort</span>
                        </div>
                    </div>

                    {/* Dashboard — full data table on desktop, card list on mobile */}
                    <div className="rounded-2xl border border-white/[0.07] bg-white/[0.012] overflow-hidden">
                        <div className="hidden md:block overflow-x-auto">
                            <div className="min-w-[1400px]">
                                <TableHeader sort={sort} onSort={toggleSort} />
                                {shown.length === 0 ? (
                                    <div className="py-16 text-center text-piggy-cream/40 text-sm">No tokens match this filter.</div>
                                ) : (
                                    <>
                                        <div className="divide-y divide-white/[0.04]">
                                            {freeRows.map((t, i) => (
                                                <TokenRow key={t.token} t={t} rank={i + 1}
                                                    expanded={expanded === t.token} onToggle={() => toggleExpand(t.token)} />
                                            ))}
                                        </div>

                                        {/* THE GATE: the rest of the feed fades out unless unlocked */}
                                        {!unlocked && fadedRows.length > 0 && (
                                            <div className="relative">
                                                <div aria-hidden
                                                    className="divide-y divide-white/[0.04] pointer-events-none select-none [mask-image:linear-gradient(to_bottom,rgba(0,0,0,1),rgba(0,0,0,0.12)_58%,transparent)] [-webkit-mask-image:linear-gradient(to_bottom,rgba(0,0,0,1),rgba(0,0,0,0.12)_58%,transparent)]">
                                                    {fadedRows.map((t, i) => (
                                                        <TokenRow key={t.token} t={t} rank={freeCount + i + 1} locked
                                                            expanded={false} onToggle={() => {}} />
                                                    ))}
                                                </div>
                                                <div className="absolute inset-0 flex items-start justify-center pt-4 px-4">
                                                    <UnlockCard access={access} auth={auth} pricing={data.pricing} />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Mobile: compact cards, tap to expand the same detail panel */}
                        <div className="md:hidden">
                            {shown.length === 0 ? (
                                <div className="py-16 text-center text-piggy-cream/40 text-sm">No tokens match this filter.</div>
                            ) : (
                                <>
                                    <div className="divide-y divide-white/[0.04]">
                                        {freeRows.map((t, i) => (
                                            <TokenCard key={t.token} t={t} rank={i + 1}
                                                expanded={expanded === t.token} onToggle={() => toggleExpand(t.token)} />
                                        ))}
                                    </div>
                                    {!unlocked && fadedRows.length > 0 && (
                                        <div className="relative">
                                            <div aria-hidden
                                                className="divide-y divide-white/[0.04] pointer-events-none select-none [mask-image:linear-gradient(to_bottom,rgba(0,0,0,1),rgba(0,0,0,0.1)_50%,transparent)] [-webkit-mask-image:linear-gradient(to_bottom,rgba(0,0,0,1),rgba(0,0,0,0.1)_50%,transparent)]">
                                                {fadedRows.map((t, i) => (
                                                    <TokenCard key={t.token} t={t} rank={freeCount + i + 1} locked
                                                        expanded={false} onToggle={() => {}} />
                                                ))}
                                            </div>
                                            <div className="absolute inset-0 flex items-start justify-center pt-2 px-3">
                                                <UnlockCard access={access} auth={auth} pricing={data.pricing} />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Disclaimer — footer only */}
            <div className="mt-10">
                <Disclaimer />
            </div>
        </section>
    );
}

// ============================================================================
// Chain sentiment — aggregate mood strip (facts, never a market call)
// ============================================================================

const SENTIMENT_STYLE: Record<ChainSentiment["label"], { label: string; cls: string; dot: string }> = {
    bullish: { label: "Bullish",  cls: "bg-piggy-gold-500/12 text-piggy-gold-300 ring-piggy-gold-500/30", dot: "bg-piggy-gold-400" },
    neutral: { label: "Neutral",  cls: "bg-white/[0.05] text-piggy-cream/70 ring-white/10",               dot: "bg-piggy-cream/40" },
    bearish: { label: "Bearish",  cls: "bg-red-500/12 text-red-300 ring-red-500/25",                      dot: "bg-red-400" },
};

function SentimentBar({ s }: { s: ChainSentiment }) {
    const st = SENTIMENT_STYLE[s.label];
    return (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-white/[0.06] bg-white/[0.015] px-3.5 py-2.5">
            <Hint text={`Aggregated across ${s.tokens} traded tokens, weighted by 24h volume so big markets count more. Descriptive only — never a prediction or advice.`}>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ring-1 ${st.cls} underline decoration-dotted decoration-current/30 underline-offset-2`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    Chain sentiment: {st.label}
                </span>
            </Hint>
            <span className="text-[12px] text-piggy-cream/55 tabular-nums">
                vol-weighted {s.vwChange24h >= 0 ? "+" : ""}{s.vwChange24h.toFixed(1)}% (24h)
            </span>
            <span className="text-piggy-cream/20 hidden sm:inline">·</span>
            <span className="text-[12px] text-piggy-cream/55 tabular-nums">{s.gainersPct}% of tokens up</span>
            {s.avgBuyPressure != null && (
                <>
                    <span className="text-piggy-cream/20 hidden sm:inline">·</span>
                    <span className="text-[12px] text-piggy-cream/55 tabular-nums">avg buy pressure {s.avgBuyPressure}%</span>
                </>
            )}
        </div>
    );
}

// ============================================================================
// Stats ribbon — aggregate dashboard metrics across the whole feed
// ============================================================================

function StatsRibbon({ feed }: { feed: ScannerToken[] }) {
    const s = useMemo(() => {
        const vol = feed.reduce((a, t) => a + (t.volumeH24 ?? 0), 0);
        const liq = feed.reduce((a, t) => a + (t.liquidityUsd ?? 0), 0);
        const holders = feed.reduce((a, t) => a + (t.holderCount ?? 0), 0);
        const avgScore = feed.length ? Math.round(feed.reduce((a, t) => a + t.score, 0) / feed.length) : 0;
        const gainers = feed.filter((t) => (t.priceChangeH24 ?? 0) > 0).length;
        const fresh = feed.filter((t) => t.ageHours != null && t.ageHours < 48).length;
        return { vol, liq, holders, avgScore, gainers, losers: feed.length - gainers, fresh, n: feed.length };
    }, [feed]);

    const tiles: { label: string; value: string; tone?: string }[] = [
        { label: "Tokens", value: String(s.n) },
        { label: "24h Volume", value: fmtUsd(s.vol) },
        { label: "Liquidity", value: fmtUsd(s.liq) },
        { label: "Holders", value: fmtCompactNum(s.holders) },
        { label: "Avg score", value: String(s.avgScore) },
        { label: "Gainers", value: `${s.gainers}/${s.n}`, tone: "text-piggy-gold-400" },
        { label: "New <48h", value: String(s.fresh), tone: "text-piggy-pink-200" },
    ];
    return (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-4">
            {tiles.map((t) => (
                <div key={t.label} className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-3 py-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-piggy-cream/40 mb-0.5">{t.label}</div>
                    <div className={`display text-lg font-black tabular-nums leading-none ${t.tone ?? "text-piggy-cream"}`}>{t.value}</div>
                </div>
            ))}
        </div>
    );
}

// Infinite-scroll trending ticker — a single-row auto-marquee (label inline, no
// separate header) that loops seamlessly by animating a duplicated track from 0
// to -50%. Pauses on hover. Uses framer's `animate` (always running — not
// whileInView, which breaks late-mounted nodes).
function TrendingRow({ tokens }: { tokens: ScannerToken[] }) {
    const [paused, setPaused] = useState(false);
    const track = [...tokens, ...tokens]; // duplicate so the loop is seamless
    const duration = Math.max(24, tokens.length * 2.4);
    return (
        <div className="mb-4 flex items-center gap-2.5">
            <span className="flex-shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-piggy-pink-300">
                <Flame className="w-3.5 h-3.5" /> Trending
            </span>
            <div className="relative flex-1 min-w-0 overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.012] py-1.5
                            [mask-image:linear-gradient(to_right,transparent,black_3%,black_97%,transparent)]"
                onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                <motion.div className="flex gap-2 w-max px-2"
                    animate={{ x: paused ? undefined : ["0%", "-50%"] }}
                    transition={{ duration, ease: "linear", repeat: Infinity }}>
                    {track.map((t, i) => (
                        <a key={`${t.token}-${i}`} href={t.dexScreenerUrl || t.flapUrl || `${EXPLORER_TOKEN}/${t.token}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] hover:border-white/[0.2] px-2.5 py-1 transition-colors">
                            <TokenLogo logo={t.logo} symbol={t.symbol} size={16} />
                            <span className="text-[13px] font-bold text-piggy-cream">{t.symbol}</span>
                            <span className={`text-[11px] font-semibold tabular-nums ${TIER[t.tier].text}`}>{t.score}</span>
                            <span className={`text-[11px] tabular-nums ${t.priceChangeH24 != null && t.priceChangeH24 >= 0 ? "text-piggy-gold-400" : "text-red-400"}`}>
                                {fmtPct(t.priceChangeH24)}
                            </span>
                        </a>
                    ))}
                </motion.div>
            </div>
        </div>
    );
}

// ============================================================================
// Table
// ============================================================================

function TableHeader({ sort, onSort }: { sort: SortKey; onSort: (k?: SortKey) => void }) {
    return (
        <div className={`${GRID} h-9 border-b border-white/[0.08] bg-white/[0.02] text-[10px] font-bold uppercase tracking-wider text-piggy-cream/40`}>
            {COLS.map((c) => {
                const active = c.sort && sort === c.sort;
                const justify = c.align === "right" ? "justify-end" : c.align === "center" ? "justify-center" : "justify-start";
                const inner = (
                    <button key={c.hint ? undefined : c.key} disabled={!c.sort} onClick={() => onSort(c.sort)}
                        className={`flex items-center gap-0.5 w-full ${justify} ${c.sort ? "hover:text-piggy-cream cursor-pointer" : "cursor-default"} ${active ? "text-piggy-gold-300" : ""} ${c.hint ? "underline decoration-dotted decoration-piggy-cream/25 underline-offset-2" : ""}`}>
                        {c.label}
                        {active && <ArrowDown className="w-2.5 h-2.5" />}
                    </button>
                );
                return c.hint ? <Hint key={c.key} text={c.hint} block>{inner}</Hint> : <span key={c.key} className="flex">{inner}</span>;
            })}
        </div>
    );
}

function TokenRow({ t, rank, locked, expanded, onToggle }: {
    t: ScannerToken; rank: number; locked?: boolean; expanded: boolean; onToggle: () => void;
}) {
    const tier = TIER[t.tier];
    const up = (t.priceChangeH24 ?? 0) >= 0;
    const bp = t.signals.buyPressureH24;
    const hDir = t.trends.holders.dir;
    const flags = t.criticalFlags.length + t.headlineFlags.length;

    return (
        <div>
            <div className={`${GRID} h-14 text-sm hover:bg-white/[0.025] transition-colors ${expanded ? "bg-white/[0.03]" : ""} ${locked ? "" : "cursor-pointer"}`}
                onClick={locked ? undefined : onToggle}>
                {/* rank */}
                <span className="text-right text-[11px] tabular-nums text-piggy-cream/35">{rank}</span>

                {/* token */}
                <div className="flex items-center gap-2 min-w-0">
                    <TokenLogo logo={t.logo} symbol={t.symbol} size={30} />
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <span className="font-black text-piggy-cream truncate leading-tight">{t.symbol}</span>
                            {t.pick && (
                                <Hint text="Radar Pick — one of the 3 strongest score + buy-pressure + momentum combinations in this scan. A data highlight, not financial advice.">
                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-px rounded bg-piggy-gold-500/25 text-piggy-gold-200 ring-1 ring-piggy-gold-500/40">
                                        <Star className="w-2.5 h-2.5 fill-current" /> PICK
                                    </span>
                                </Hint>
                            )}
                            {t.category === "bonding" ? (
                                <Hint text={`Still on the ${t.launchpad} bonding curve — not on the DEX yet. The % shows how close it is to graduating to a real trading pair.`}>
                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-px rounded bg-piggy-pink-500/25 text-piggy-pink-100 ring-1 ring-piggy-pink-500/40">
                                        <GraduationCap className="w-2.5 h-2.5" />{t.flap?.progress != null ? ` ${Math.round(t.flap.progress)}%` : " CURVE"}
                                    </span>
                                </Hint>
                            ) : t.ageHours != null && t.ageHours < 6 ? (
                                <span className="text-[9px] font-bold px-1 py-px rounded bg-piggy-pink-500/30 text-piggy-pink-100 animate-pulse">⚡ NEW</span>
                            ) : (
                                <span className={`text-[9px] font-bold px-1 py-px rounded ${t.category === "new-launch" ? "bg-piggy-pink-500/20 text-piggy-pink-200" : "bg-white/[0.06] text-piggy-cream/40"}`}>
                                    {CATEGORY_LABEL[t.category]}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-piggy-cream/35 leading-tight" onClick={(e) => e.stopPropagation()}>
                            <span className="truncate">{shortAddr(t.token)}</span>
                            <CopyAddr address={t.token} />
                        </div>
                    </div>
                </div>

                {/* score */}
                <div className="flex justify-center">
                    <span className={`inline-flex items-center justify-center min-w-[30px] px-1.5 py-0.5 rounded-md text-xs font-black tabular-nums ring-1 ${tier.chip}`}>{t.score}</span>
                </div>

                {/* price */}
                <span className="text-right tabular-nums text-piggy-cream/85 text-[13px]">{fmtPrice(t.priceUsd)}</span>

                {/* market cap */}
                <span className="text-right tabular-nums text-piggy-cream/70 text-[13px]">{fmtUsd(t.marketCap)}</span>

                {/* 24h change */}
                <span className={`text-right tabular-nums text-[13px] font-semibold ${up ? "text-piggy-gold-400" : "text-red-400"}`}>{fmtPct(t.priceChangeH24)}</span>

                {/* vol */}
                <span className="text-right tabular-nums text-piggy-cream/70 text-[13px]">{fmtUsd(t.volumeH24)}</span>

                {/* liq */}
                <span className="text-right tabular-nums text-piggy-cream/70 text-[13px]">{fmtUsd(t.liquidityUsd)}</span>

                {/* holders + trend */}
                <span className="text-right tabular-nums text-piggy-cream/70 text-[13px] inline-flex items-center justify-end gap-1">
                    {t.holderCount != null ? fmtCompactNum(t.holderCount) : "—"}
                    {hDir === "up" && <ArrowUp className="w-3 h-3 text-piggy-gold-400" />}
                    {hDir === "down" && <ArrowDown className="w-3 h-3 text-red-400" />}
                </span>

                {/* buy/sell bar (bonding rows show graduation progress instead) */}
                <div className="flex items-center gap-1.5">
                    {t.category === "bonding" && t.flap?.progress != null ? (
                        <>
                            <div className="flex h-1.5 flex-1 rounded-full overflow-hidden bg-white/[0.06]">
                                <div className="bg-piggy-pink-400" style={{ width: `${Math.min(100, t.flap.progress)}%` }} />
                            </div>
                            <span className="text-[10px] tabular-nums text-piggy-pink-200/80 w-7 text-right">{Math.round(t.flap.progress)}%</span>
                        </>
                    ) : bp != null ? (
                        <>
                            <div className="flex h-1.5 flex-1 rounded-full overflow-hidden bg-white/[0.06]">
                                <div className="bg-piggy-gold-400" style={{ width: `${bp}%` }} />
                                <div className="bg-red-400/70" style={{ width: `${100 - bp}%` }} />
                            </div>
                            <span className="text-[10px] tabular-nums text-piggy-cream/50 w-7 text-right">{bp}%</span>
                        </>
                    ) : <span className="text-[11px] text-piggy-cream/30">—</span>}
                </div>

                {/* top10 concentration */}
                <span className={`text-right tabular-nums text-[12px] ${t.top10HolderPct != null && t.top10HolderPct >= 70 ? "text-amber-400" : "text-piggy-cream/60"}`}>
                    {t.top10HolderPct != null ? `${t.top10HolderPct.toFixed(0)}%` : "—"}
                </span>

                {/* tax */}
                <span className="text-right tabular-nums text-[12px] text-piggy-cream/60">
                    {t.flap && (t.flap.buyTaxBps != null || t.flap.sellTaxBps != null)
                        ? `${fmtBps(t.flap.buyTaxBps)}/${fmtBps(t.flap.sellTaxBps)}` : "—"}
                </span>

                {/* age */}
                <span className="text-right tabular-nums text-[12px] text-piggy-cream/50">{fmtAge(t.ageHours)}</span>

                {/* signal */}
                <div className="flex items-center gap-1 min-w-0">
                    {t.analysis.posture && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold truncate ${postureTone(t.analysis.posture)}`}>
                            {postureLabel(t.analysis.posture)}
                        </span>
                    )}
                    <MomentumDot momentum={t.signals.momentum} />
                </div>

                {/* actions */}
                <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {flags > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-300/90" title={`${flags} flag(s)`}>
                            <AlertTriangle className="w-3 h-3" />{flags}
                        </span>
                    )}
                    <a href={t.category === "bonding" && t.flapUrl ? t.flapUrl : `https://app.uniswap.org/swap?outputCurrency=${t.token}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[11px] font-bold text-piggy-pink-300 hover:text-piggy-pink-200 inline-flex items-center gap-0.5">
                        Buy <ArrowUpRight className="w-3 h-3" />
                    </a>
                    {!locked && <ChevronDown className={`w-3.5 h-3.5 text-piggy-cream/30 transition-transform ${expanded ? "rotate-180" : ""}`} />}
                </div>
            </div>

            {/* Expanded deep-analysis panel */}
            {expanded && !locked && <RowDetail t={t} />}
        </div>
    );
}

// Mobile card — the row's must-know facts in two lines + the buy/graduation
// bar, expanding into the same RowDetail panel as the desktop table.
function TokenCard({ t, rank, locked, expanded, onToggle }: {
    t: ScannerToken; rank: number; locked?: boolean; expanded: boolean; onToggle: () => void;
}) {
    const tier = TIER[t.tier];
    const up = (t.priceChangeH24 ?? 0) >= 0;
    const bp = t.signals.buyPressureH24;
    const bonding = t.category === "bonding";
    return (
        <div>
            <div className={`px-3 py-2.5 ${expanded ? "bg-white/[0.03]" : ""} ${locked ? "" : "active:bg-white/[0.03] cursor-pointer"}`}
                onClick={locked ? undefined : onToggle}>
                <div className="flex items-center gap-2.5">
                    <span className="w-5 text-right text-[10px] tabular-nums text-piggy-cream/35 flex-shrink-0">{rank}</span>
                    <TokenLogo logo={t.logo} symbol={t.symbol} size={34} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-black text-piggy-cream truncate leading-tight text-[14px]">{t.symbol}</span>
                            {t.pick && (
                                <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1 py-px rounded bg-piggy-gold-500/25 text-piggy-gold-200 ring-1 ring-piggy-gold-500/40 flex-shrink-0">
                                    <Star className="w-2 h-2 fill-current" /> PICK
                                </span>
                            )}
                            {bonding ? (
                                <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1 py-px rounded bg-piggy-pink-500/25 text-piggy-pink-100 flex-shrink-0">
                                    <GraduationCap className="w-2 h-2" />{t.flap?.progress != null ? `${Math.round(t.flap.progress)}%` : "CURVE"}
                                </span>
                            ) : t.ageHours != null && t.ageHours < 6 ? (
                                <span className="text-[8px] font-bold px-1 py-px rounded bg-piggy-pink-500/30 text-piggy-pink-100 flex-shrink-0">⚡ NEW</span>
                            ) : null}
                            <span className={`ml-auto inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-md text-[11px] font-black tabular-nums ring-1 flex-shrink-0 ${tier.chip}`}>{t.score}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] tabular-nums leading-tight mt-0.5">
                            <span className="text-piggy-cream/85">{fmtPrice(t.priceUsd)}</span>
                            <span className={`font-semibold ${up ? "text-piggy-gold-400" : "text-red-400"}`}>{fmtPct(t.priceChangeH24)}</span>
                            <span className="text-piggy-cream/45">V {fmtUsd(t.volumeH24)}</span>
                            <span className="text-piggy-cream/45">L {fmtUsd(t.liquidityUsd)}</span>
                            {!locked && <ChevronDown className={`ml-auto w-3.5 h-3.5 text-piggy-cream/30 transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`} />}
                        </div>
                    </div>
                </div>
                {/* slim flow / graduation strip */}
                {(bonding && t.flap?.progress != null) ? (
                    <div className="mt-1.5 ml-[64px] flex h-1 rounded-full overflow-hidden bg-white/[0.06]">
                        <div className="bg-piggy-pink-400" style={{ width: `${Math.min(100, t.flap.progress)}%` }} />
                    </div>
                ) : bp != null ? (
                    <div className="mt-1.5 ml-[64px] flex h-1 rounded-full overflow-hidden bg-white/[0.06]">
                        <div className="bg-piggy-gold-400" style={{ width: `${bp}%` }} />
                        <div className="bg-red-400/70" style={{ width: `${100 - bp}%` }} />
                    </div>
                ) : null}
            </div>
            {expanded && !locked && <RowDetail t={t} />}
        </div>
    );
}

function RowDetail({ t }: { t: ScannerToken }) {
    const chartUrl = t.pair
        ? `https://dexscreener.com/robinhood/${t.pair}?embed=1&theme=dark&trades=0&info=0`
        : null;
    const bonding = t.category === "bonding";
    const hTrend = t.trends.holders.pct != null
        ? ` (${t.trends.holders.pct >= 0 ? "+" : ""}${t.trends.holders.pct.toFixed(0)}%/${t.trends.windowHours ?? "?"}h)` : "";

    return (
        <div className="bg-white/[0.02] border-t border-white/[0.05]">
            <div className="grid lg:grid-cols-2">
                {/* Left half: live chart for DEX tokens (graduation panel for
                    bonding tokens), FLUSH — no padding, border or rounding, so
                    it fills exactly half the accordion edge-to-edge. The
                    wrapper clips ~40px off the bottom of the embed to hide the
                    DexScreener footer bar. */}
                {bonding ? (
                    <BondingPanel t={t} />
                ) : (
                    <div className="relative overflow-hidden bg-black/30 min-h-[340px] h-[340px] lg:h-auto lg:min-h-[380px] lg:border-r lg:border-white/[0.05]">
                        {chartUrl ? (
                            <iframe src={chartUrl} title={`${t.symbol} chart`} loading="lazy"
                                className="absolute inset-x-0 top-0 w-full"
                                style={{ border: 0, height: "calc(100% + 40px)" }} />
                        ) : (
                            <div className="w-full h-full grid place-items-center text-xs text-piggy-cream/40">Chart unavailable</div>
                        )}
                    </div>
                )}

                {/* Right half: the read, then the numbers, then the flags. */}
                <div className="min-w-0 px-4 py-4">
                    {/* The read — posture + what the data shows, in one line */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        {t.analysis.posture && (
                            <Hint text="A neutral one-word read of current order flow and trends — descriptive, not advice.">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold ${postureTone(t.analysis.posture)}`}>
                                    {postureLabel(t.analysis.posture)}
                                </span>
                            </Hint>
                        )}
                        {t.analysis.facts.length > 0 && (
                            <span className="text-[12px] text-piggy-cream/60 leading-relaxed">{t.analysis.facts.join(" · ")}</span>
                        )}
                    </div>

                    {/* The numbers — every metric explains itself on hover */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
                        <Stat label="Market cap" value={fmtUsd(t.marketCap)}
                            hint="Price × circulating supply. FDV (all supply) shown next." />
                        <Stat label="FDV" value={fmtUsd(t.fdv)}
                            hint="Fully-diluted value — market cap if the ENTIRE supply were circulating." />
                        <Stat label="Liquidity" value={fmtUsd(t.liquidityUsd)}
                            hint={bonding ? "Reserve currently in the bonding curve." : "Dollars pooled in the trading pair — deeper = less slippage."} />
                        <Stat label="Turnover" value={t.ratios.volToLiq != null ? `${t.ratios.volToLiq.toFixed(1)}×` : "—"}
                            tone={t.ratios.volToLiq != null && t.ratios.volToLiq >= 10 ? "hot" : undefined}
                            hint="24h volume ÷ liquidity. How hard the pool is working: 1× = traded its depth once today. Very high turnover on thin liquidity = volatile." />
                        <Stat label="Backing" value={t.ratios.liqToMcap != null ? `${t.ratios.liqToMcap.toFixed(0)}%` : "—"}
                            tone={t.ratios.liqToMcap != null && t.ratios.liqToMcap < 2 ? "warn" : undefined}
                            hint="Liquidity ÷ market cap. How much real depth stands behind the valuation — under ~2% means the price rests on very little actual money." />
                        <Stat label="Txns 24h" value={t.txnsH24 != null ? fmtCompactNum(t.txnsH24) : "—"}
                            hint="Total buys + sells in the last 24 hours." />
                        <Stat label="Buy pressure" value={t.signals.buyPressureH24 != null ? `${t.signals.buyPressureH24}%` : "—"}
                            tone={t.signals.buyPressureH24 == null ? undefined : t.signals.buyPressureH24 >= 55 ? "good" : t.signals.buyPressureH24 <= 45 ? "bad" : undefined}
                            hint="Share of 24h trades that were buys. A fact about flow, not a signal." />
                        <Stat label="Holders" value={`${t.holderCount != null ? fmtCompactNum(t.holderCount) : "—"}${hTrend}`}
                            tone={t.trends.holders.dir === "up" ? "good" : t.trends.holders.dir === "down" ? "bad" : undefined}
                            hint="Wallets holding the token, with the recent trend in parentheses." />
                        <Stat label="Top 1 / 10" value={`${t.topHolderPct != null ? t.topHolderPct.toFixed(0) + "%" : "—"} / ${t.top10HolderPct != null ? t.top10HolderPct.toFixed(0) + "%" : "—"}`}
                            tone={t.topHolderPct != null && t.topHolderPct >= 50 ? "warn" : undefined}
                            hint="Supply held by the largest wallet / largest 10 (LP, burn and launchpad vault excluded). Concentrated supply = a few wallets can dump." />
                        <Stat label="1h / 24h"
                            valueNode={<><PctSpan v={t.priceChangeH1} /><span className="text-piggy-cream/30"> / </span><PctSpan v={t.priceChangeH24} /></>}
                            hint="Price change over the last hour and day." />
                        <Stat label="Age" value={fmtAge(t.ageHours)}
                            hint={bonding ? "Time since the token launched on the bonding curve." : "Time since the trading pair was created."} />
                        {t.flap && (t.flap.buyTaxBps != null || t.flap.sellTaxBps != null) && (
                            <Stat label="Tax buy/sell" value={`${fmtBps(t.flap.buyTaxBps)} / ${fmtBps(t.flap.sellTaxBps)}`}
                                hint="Launchpad fee taken on every buy / sell." />
                        )}
                    </div>

                    {/* Badges + socials */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        {t.flap?.onLaunchpad && !bonding && <Tag>⚡ Flap launchpad</Tag>}
                        {t.flap?.isLowRisk && (
                            <Hint text="flap.sh's own low-risk tag (their assessment, not ours).">
                                <Tag tone="gold">Flap: low-risk</Tag>
                            </Hint>
                        )}
                        {t.flap?.isInnovation && <Tag>Innovation</Tag>}
                        <SocialLinks socials={t.socials} />
                    </div>

                    {/* Flags — worst first */}
                    {t.criticalFlags.map((f, i) => (
                        <div key={i} className="text-[12px] text-red-300 font-semibold flex items-start gap-1.5 mb-1">
                            <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" /><span>{f}</span>
                        </div>
                    ))}
                    {t.headlineFlags.length > 0 && (
                        <div className="space-y-1.5 mb-3">
                            {t.headlineFlags.map((f, i) => <FlagPill key={`${f.label}-${i}`} flag={f} />)}
                        </div>
                    )}

                    <div className="flex items-center gap-3 pt-1">
                        <AddressChip address={t.token} />
                        {t.dexScreenerUrl && <a href={t.dexScreenerUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-piggy-cream/50 hover:text-piggy-cream">DexScreener ↗</a>}
                        {t.flapUrl && <a href={t.flapUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-piggy-cream/50 hover:text-piggy-cream">flap.sh ↗</a>}
                        <a href={`${EXPLORER_TOKEN}/${t.token}`} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-piggy-cream/50 hover:text-piggy-cream">Explorer ↗</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

// One metric cell in the detail grid — label + value + self-explaining tooltip.
// `tone` colors the whole value; `valueNode` allows mixed-color values (e.g.
// the green/red 1h / 24h pair).
function Stat({ label, value, valueNode, hint, tone }: {
    label: string; value?: string; valueNode?: React.ReactNode; hint: string;
    tone?: "warn" | "hot" | "good" | "bad";
}) {
    const valueCls =
        tone === "warn" ? "text-amber-300"
        : tone === "hot" ? "text-piggy-pink-200"
        : tone === "good" ? "text-piggy-gold-400"
        : tone === "bad" ? "text-red-400"
        : "text-piggy-cream/90";
    return (
        <Hint text={hint} block>
            <div className="w-full rounded-lg bg-white/[0.02] ring-1 ring-white/[0.05] px-2.5 py-1.5 hover:ring-white/[0.12] transition-shadow">
                <div className="text-[9px] uppercase tracking-wider text-piggy-cream/40 leading-tight underline decoration-dotted decoration-piggy-cream/20 underline-offset-2">{label}</div>
                <div className={`text-[13px] font-bold tabular-nums leading-snug ${valueCls}`}>{valueNode ?? value}</div>
            </div>
        </Hint>
    );
}

// Green/red % span — the standard up/down tint used across the dashboard.
function PctSpan({ v }: { v: number | null }) {
    if (v == null) return <span className="text-piggy-cream/40">—</span>;
    return <span className={v >= 0 ? "text-piggy-gold-400" : "text-red-400"}>{fmtPct(v)}</span>;
}

// Graduation panel — shown in place of the chart for bonding-curve tokens.
// Flush like the chart pane: fills its half edge-to-edge, padding inside only.
function BondingPanel({ t }: { t: ScannerToken }) {
    const pct = t.flap?.progress != null ? Math.min(100, Math.max(0, t.flap.progress)) : null;
    return (
        <div className="bg-piggy-pink-500/[0.04] min-h-[340px] lg:min-h-[380px] lg:border-r lg:border-white/[0.05] p-6 flex flex-col justify-center gap-4">
            <div className="flex items-center gap-2 text-piggy-pink-200">
                <GraduationCap className="w-5 h-5" />
                <span className="display text-lg font-black">Bonding curve — pre-DEX</span>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-piggy-cream/40">{t.launchpad}</span>
            </div>
            <p className="text-[13px] text-piggy-cream/65 leading-relaxed">
                This token is still on the {t.launchpad} launchpad curve. When the curve fills to 100%,
                its liquidity graduates into a real DEX pair and it starts trading on the open market —
                the earliest (and riskiest) stage to be in.
            </p>
            {pct != null && (
                <div>
                    <div className="flex items-baseline justify-between mb-1.5">
                        <span className="text-[11px] uppercase tracking-wider text-piggy-cream/45 font-bold">Graduation progress</span>
                        <span className="display text-2xl font-black text-piggy-pink-200 tabular-nums">{pct.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 rounded-full bg-white/[0.06] overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-piggy-pink-500 to-piggy-gold-400 transition-[width]" style={{ width: `${pct}%` }} />
                    </div>
                </div>
            )}
            {t.flapUrl && (
                <a href={t.flapUrl} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center !py-2.5 mt-1">
                    View on {t.launchpad} <ArrowUpRight className="w-4 h-4" />
                </a>
            )}
        </div>
    );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: "pink" | "gold" }) {
    const cls = tone === "pink" ? "bg-piggy-pink-500/15 text-piggy-pink-200"
        : tone === "gold" ? "bg-piggy-gold-400/15 text-piggy-gold-300"
        : "bg-white/[0.06] text-piggy-cream/60";
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{children}</span>;
}

// ============================================================================
// The gate — unlock card shown over the fading feed
// ============================================================================

function UnlockCard({ access, auth, pricing }: {
    access: ReturnType<typeof useRadarAccess>;
    auth: ReturnType<typeof useRadarAuth>;
    pricing: RadarPricing | null;
}) {
    // Direct stake + the PIGGY inside any locked-LP position both count.
    const totalEquiv = access.stakedPiggy + access.lpPiggyEquiv;
    const shortBy = access.threshold > totalEquiv ? access.threshold - totalEquiv : 0n;
    const usd = pricing && pricing.unlockUsd > 0 ? `$${pricing.unlockUsd}` : null;
    const stakeAmt = pricing && pricing.stakePiggy > 0 ? fmtCompactPiggy(String(pricing.stakePiggy)) : fmtCompactPiggyBig(access.threshold);
    const ethAmt = pricing && pricing.ethAmount > 0 ? pricing.ethAmount : 0;
    const canPayEth = ethAmt > 0 && !!pricing?.creatorWallet;

    return (
        <div className="glass-pink rounded-2xl p-6 max-w-lg w-full text-center shadow-glow">
            <div className="w-11 h-11 rounded-2xl bg-piggy-pink-500/20 text-piggy-pink-300 grid place-items-center mx-auto mb-3">
                <Lock className="w-5 h-5" />
            </div>
            <h3 className="display text-xl font-black mb-1.5">
                Unlock the full dashboard{usd && <span className="text-piggy-pink-200"> — {usd}</span>}
            </h3>
            <p className="text-sm text-piggy-cream/70 mb-4">
                The whole feed — every token's full score, buy/sell pressure, holder &amp;
                concentration data, live trends, and brand-new liquidity the moment it lands.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
                <a href="/stake" className="btn-primary justify-center flex-col !py-2.5 leading-tight">
                    <span>Stake {stakeAmt} PIGGY</span>
                    <span className="text-[10px] font-semibold opacity-70">≈ {usd ?? "—"} · keep your tokens</span>
                </a>
                <button onClick={() => canPayEth && auth.payEth(pricing!.creatorWallet, ethAmt)}
                    disabled={auth.ethPaying || !canPayEth}
                    className="btn-ghost justify-center flex-col !py-2.5 leading-tight disabled:opacity-40 disabled:cursor-not-allowed">
                    <span>{auth.ethPaying ? "Confirming…" : `Pay ${ethAmt > 0 ? ethAmt.toFixed(4) : "—"} ETH`}</span>
                    <span className="text-[10px] font-semibold opacity-70">≈ {usd ?? "—"} · instant access</span>
                </button>
            </div>
            <p className="mt-3 text-xs text-piggy-cream/50 leading-relaxed">
                {access.connected && totalEquiv > 0n && shortBy > 0n
                    ? `You have ${fmtCompactPiggyBig(totalEquiv)} PIGGY staked (incl. LP equivalent) — ${fmtCompactPiggyBig(shortBy)} more to unlock.`
                    : "Stake PIGGY — directly or as locked LP (the PIGGY inside your LP counts) — or pay ETH. Any one unlocks the full feed."}
                {" "}Already staked or paid?{" "}
                <button onClick={auth.unlock} disabled={auth.busy} className="text-piggy-pink-300 hover:text-piggy-pink-200 font-semibold disabled:opacity-40">
                    {auth.busy ? "Verifying…" : "Unlock (sign)"}
                </button>
            </p>
            {(auth.error || auth.ethError) && <p className="mt-2 text-xs text-amber-400/90">{auth.ethError ?? auth.error}</p>}
        </div>
    );
}

// ============================================================================
// Small shared pieces
// ============================================================================

// Old scans may still carry the long posture labels — map them to the short
// spellings so the Signal chip never clips regardless of feed vintage.
function postureLabel(posture: string): string {
    if (posture === "Bid strengthening") return "Firming";
    if (posture === "Losing steam") return "Fading";
    return posture;
}

function postureTone(posture: string): string {
    const p = posture.toLowerCase();
    // Old label spellings kept so cached rows from a previous scan still tint.
    if (p === "accumulating" || p === "heating up" || p === "firming" || p === "bid strengthening")
        return "bg-piggy-gold-500/15 text-piggy-gold-300 ring-1 ring-piggy-gold-500/30";
    if (p === "distributing" || p === "cooling" || p === "fading" || p === "losing steam")
        return "bg-red-500/12 text-red-300 ring-1 ring-red-500/25";
    return "bg-white/[0.05] text-piggy-cream/60 ring-1 ring-white/10";
}

// Lightweight hover/focus tooltip — pure CSS, no portal. Used on headers,
// badges and detail-panel metrics so every number explains itself.
function Hint({ text, children, block }: { text: string; children: React.ReactNode; block?: boolean }) {
    return (
        <span tabIndex={0} className={`relative group/hint cursor-help outline-none ${block ? "flex min-w-0" : "inline-flex items-center"}`}>
            {children}
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-60 rounded-lg bg-[#16090f] ring-1 ring-piggy-pink-500/30 px-3 py-2 text-[11px] leading-snug text-piggy-cream/85 opacity-0 invisible group-hover/hint:opacity-100 group-hover/hint:visible group-focus-within/hint:opacity-100 group-focus-within/hint:visible transition-opacity duration-150 z-40 normal-case font-normal tracking-normal text-left shadow-2xl">
                {text}
            </span>
        </span>
    );
}

function MomentumDot({ momentum }: { momentum: Momentum }) {
    const m = MOMENTUM[momentum];
    const Icon = m.icon;
    if (!Icon && momentum === "flat") return null;
    return (
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 flex-shrink-0 ${m.chip}`}>
            {Icon && <Icon className="w-2.5 h-2.5" />}{m.label}
        </span>
    );
}

function TokenLogo({ logo, symbol, size = 30 }: { logo: string | null; symbol: string; size?: number }) {
    const [failed, setFailed] = useState(false);
    const monogram = symbol.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
    if (logo && !failed) {
        return (
            <img src={logo} alt={symbol} onError={() => setFailed(true)}
                width={size} height={size} loading="lazy" draggable={false}
                className="rounded-full object-cover bg-black/30 ring-1 ring-white/10 flex-shrink-0"
                style={{ width: size, height: size }} />
        );
    }
    return (
        <div className="rounded-full grid place-items-center flex-shrink-0 bg-piggy-pink-500/20 ring-1 ring-piggy-pink-500/40"
            style={{ width: size, height: size }} aria-label={symbol}>
            <span className="display font-black text-piggy-pink-200 leading-none" style={{ fontSize: size * 0.4 }}>{monogram}</span>
        </div>
    );
}

function SocialLinks({ socials }: { socials: ScannerSocial[] }) {
    if (socials.length === 0) return null;
    const iconFor = (type: string) => {
        const t = type.toLowerCase();
        if (t.includes("twitter") || t === "x") return Twitter;
        if (t.includes("telegram")) return Send;
        return Globe;
    };
    return (
        <div className="flex items-center gap-1.5">
            {socials.slice(0, 3).map((s, i) => {
                const Icon = iconFor(s.type);
                return (
                    <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.type}
                        className="text-piggy-cream/40 hover:text-piggy-cream transition-colors">
                        <Icon className="w-3.5 h-3.5" />
                    </a>
                );
            })}
        </div>
    );
}

function FlagPill({ flag }: { flag: ScannerFlag }) {
    const [open, setOpen] = useState(false);
    const bad = flag.status === "bad";
    const Icon = bad ? XCircle : AlertTriangle;
    const tone = bad ? "text-red-300" : "text-amber-300";
    return (
        <div>
            <button onClick={() => flag.detail && setOpen((o) => !o)}
                className={`w-full flex items-center gap-1.5 text-left text-[12px] font-semibold ${tone}`} aria-expanded={open}>
                <Icon className="w-3 h-3 flex-shrink-0" />
                <span className="flex-1 min-w-0">{flag.label}</span>
                {flag.detail && <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />}
            </button>
            {open && flag.detail && <div className="mt-1 text-[12px] leading-relaxed text-piggy-cream/55">{flag.detail}</div>}
        </div>
    );
}

// Icon-only copy button for the contract address (shown on every row).
function CopyAddr({ address }: { address: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch { /* clipboard blocked */ }
    };
    return (
        <button onClick={copy} aria-label={copied ? "Contract copied" : "Copy contract address"}
            className="text-piggy-cream/35 hover:text-piggy-pink-300 transition-colors flex-shrink-0">
            {copied ? <Check className="w-3 h-3 text-piggy-pink-300" /> : <Copy className="w-3 h-3" />}
        </button>
    );
}

function AddressChip({ address }: { address: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch { /* clipboard blocked */ }
    };
    return (
        <button onClick={copy} aria-label={copied ? "Address copied" : "Copy token address"}
            className="group inline-flex items-center gap-1 font-mono text-[11px] text-piggy-cream/45 hover:text-piggy-cream transition-colors">
            {shortAddr(address)}
            {copied ? <Check className="w-2.5 h-2.5 text-piggy-pink-300" /> : <Copy className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />}
        </button>
    );
}

// ============================================================================
// Server-locked state (gating enabled server-side) + states + disclaimer
// ============================================================================

function LockedState({ counts, auth }: { counts: ScannerFeed["counts"]; auth: ReturnType<typeof useRadarAuth> }) {
    const teases: string[] = [];
    if (counts.established) teases.push(`${counts.established} established`);
    if (counts.newLaunch) teases.push(`${counts.newLaunch} new`);
    if (counts.hiddenGem) teases.push(`${counts.hiddenGem} gems`);
    const teaseLine = teases.length ? `${teases.join(" · ")} found` : "New tokens found";
    return (
        <div className="relative">
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.012] divide-y divide-white/[0.04] blur-sm pointer-events-none select-none opacity-50" aria-hidden>
                {Array.from({ length: 7 }).map((_, i) => <div key={i} className="h-14" />)}
            </div>
            <div className="absolute inset-0 grid place-items-center px-4">
                <div className="glass-pink rounded-3xl p-8 max-w-md w-full text-center shadow-glow">
                    <div className="w-14 h-14 rounded-2xl bg-piggy-pink-500/20 text-piggy-pink-300 grid place-items-center mx-auto mb-4">
                        <Lock className="w-6 h-6" />
                    </div>
                    <h2 className="display text-2xl font-black mb-2">Unlock Piggy Radar</h2>
                    <p className="text-sm text-piggy-cream/60 mb-1">Stake PIGGY or pay to access the full alpha feed.</p>
                    <p className="text-xs text-piggy-pink-200 font-semibold mb-6 tabular-nums">{teaseLine} — unlock to see them.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <button onClick={auth.unlock} disabled={auth.busy}
                            className="btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                            {auth.busy ? "Verifying…" : "Unlock (sign)"}
                            {!auth.busy && <ArrowRight className="w-4 h-4" />}
                        </button>
                        <a href="/stake" className="btn-ghost justify-center">Stake PIGGY</a>
                    </div>
                    {auth.error && <p className="mt-4 text-xs text-amber-400/90">{auth.error}</p>}
                    {auth.requirements && (
                        <p className="mt-2 text-xs text-piggy-cream/50 leading-relaxed">
                            Unlock by staking ≥ {fmtCompactPiggy(auth.requirements.stakePiggy)} PIGGY
                            {auth.stakedPiggy != null && ` (you have ${fmtCompactPiggy(auth.stakedPiggy)})`}{" "}
                            or sending {auth.requirements.ethPrice} ETH to the creator wallet.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function Disclaimer() {
    return (
        <div className="glass-pink rounded-2xl p-4 flex gap-3 items-start">
            <ShieldAlert className="w-4 h-4 text-piggy-pink-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-piggy-cream/70 leading-relaxed">
                <span className="font-semibold text-piggy-cream">Not financial advice.</span>{" "}
                Piggy Radar screens for common risk factors and shows facts — it is{" "}
                <span className="font-semibold">not</span> a guarantee, prediction, or
                recommendation to buy, sell, or hold anything. Momentum and buy-pressure
                are neutral facts, not signals a token "will pump." Scores are
                preliminary; a token can pass screening and still rug later. Always do
                your own research.
            </div>
        </div>
    );
}

function SkeletonTable() {
    return (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.012] divide-y divide-white/[0.04] overflow-hidden">
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse bg-white/[0.01]" />
            ))}
        </div>
    );
}

function EmptyState({ error }: { error?: string }) {
    return (
        <div className="py-16 text-center">
            {error ? (
                <>
                    <AlertCircle className="w-6 h-6 text-amber-400 mx-auto mb-3" />
                    <div className="text-piggy-cream/70 mb-1">The scanner is catching its breath.</div>
                    <div className="text-sm text-piggy-cream/40">We couldn&apos;t reach the feed just now — it retries automatically every minute.</div>
                </>
            ) : (
                <>
                    <Loader2 className="w-6 h-6 text-piggy-cream/40 mx-auto mb-3 animate-spin" />
                    <div className="text-piggy-cream/70 mb-1">No vetted tokens right now.</div>
                    <div className="text-sm text-piggy-cream/40 max-w-md mx-auto">
                        The scan filters out rugged/honeypot tokens, so quiet periods can be empty. Check back soon.
                    </div>
                </>
            )}
        </div>
    );
}

// ============================================================================
// Formatting helpers
// ============================================================================

function shortAddr(a: string): string { return `${a.slice(0, 6)}…${a.slice(-4)}`; }

function relTime(ms: number): string {
    if (!ms) return "just now";
    const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
    if (s < 45) return "just now";
    if (s < 90) return "a minute ago";
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
}

function fmtPrice(v: number | null): string {
    if (v == null) return "—";
    if (v === 0) return "$0";
    if (v >= 1) return `$${v.toFixed(2)}`;
    if (v >= 0.01) return `$${v.toFixed(4)}`;
    const exp = Math.floor(Math.log10(v));
    const digits = Math.min(12, Math.max(4, -exp + 2));
    return `$${v.toFixed(digits)}`;
}

function fmtBps(bps: number | null): string {
    if (bps == null) return "?";
    return `${+(bps / 100).toFixed(2)}%`;
}

function fmtUsd(v: number | null): string {
    if (v == null) return "—";
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
    return `$${Math.round(v)}`;
}

function fmtCompactNum(v: number): string {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return String(v);
}

function fmtPct(v: number | null): string {
    if (v == null) return "—";
    // Fresh launches post +100,000%-style moves — compact them so cells and
    // the ticker stay readable ("+112.6K%" instead of "+112620.0%").
    if (Math.abs(v) >= 1000) return `${v >= 0 ? "+" : "-"}${fmtCompactNum(Math.abs(v))}%`;
    return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function fmtAge(hours: number | null): string {
    if (hours == null) return "—";
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
    if (hours < 48) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
}
